import * as THREE from 'three';
import { EffectComposer }  from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass }      from 'three/addons/postprocessing/RenderPass.js';
import { SSAOPass }        from 'three/addons/postprocessing/SSAOPass.js';
import { OutputPass }      from 'three/addons/postprocessing/OutputPass.js';

import { createScene }       from './scene.js';
import { createCamera }      from './camera.js';
import { initUI }            from './ui.js';
import { Controls }          from './car/controls.js';
import { Car }               from './car/car.js';
import { CarMesh }           from './car/carMesh.js';
import { fetchOSMData }      from './osm/fetcher.js';
import { parseOSMData }      from './osm/parser.js';
import { createProjection }  from './osm/projection.js';
import { fetchElevation }    from './osm/elevation.js';
import { buildRoadMesh }     from './geometry/roads.js';
import { buildBuildingMesh } from './geometry/buildings.js';
import { buildAreaMesh, buildWaterMesh } from './geometry/areas.js';
import { createTerrain }     from './geometry/terrain.js';
import { buildTreeMesh }     from './geometry/trees.js';
import { buildRailwayMesh }  from './geometry/railways.js';
import { buildBarrierMesh }  from './geometry/barriers.js';
import { buildBridgeMesh }   from './geometry/bridges.js';
import { buildAmenityMesh }        from './geometry/amenities.js';
import { buildSeaMesh }            from './geometry/sea.js';
import { buildTrafficLightsMesh }  from './geometry/traffic_lights.js';
import { Traffic }           from './traffic.js';
import { Minimap }           from './minimap.js';
import { DayNightCycle }     from './daynight.js';
import { Weather }           from './weather.js';
import {
  DEFAULT_LAT,
  DEFAULT_LNG,
  LOAD_RADIUS_METERS,
  WORLD_RELOAD_TRIGGER,
} from './constants.js';

// ── Scene setup ───────────────────────────────────────────────────────────────
const { scene, renderer, sun, ambient, hemi } = createScene();
const camera   = createCamera();
const controls = new Controls();
const car      = new Car();
const carMesh  = new CarMesh();
const traffic  = new Traffic();
const daynight = new DayNightCycle(scene, sun, ambient, hemi);
const weather  = new Weather(scene);

scene.add(carMesh.group);
scene.add(traffic.mesh);

// Terrain + sea are rebuilt on every loadArea(); start with a flat placeholder
let terrainMesh = createTerrain();
let seaMesh     = null;
scene.add(terrainMesh);

// ── Post-processing (SSAO) ────────────────────────────────────────────────────
let composer     = null;
let ssaoPass     = null;
let useSSAO      = false; // toggled via settings

function _buildComposer() {
  composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera.threeCamera));

  ssaoPass = new SSAOPass(scene, camera.threeCamera, window.innerWidth, window.innerHeight);
  ssaoPass.kernelRadius  = 8;
  ssaoPass.minDistance   = 0.002;
  ssaoPass.maxDistance   = 0.08;
  ssaoPass.output        = SSAOPass.OUTPUT.Default;
  composer.addPass(ssaoPass);

  composer.addPass(new OutputPass());
}

// Mini-map
const minimapCanvas = document.getElementById('minimap-canvas');
const minimap = minimapCanvas ? new Minimap(minimapCanvas) : null;

// ── Window resize ─────────────────────────────────────────────────────────────
window.addEventListener('resize', () => {
  renderer.setSize(window.innerWidth, window.innerHeight);
  camera.threeCamera.aspect = window.innerWidth / window.innerHeight;
  camera.threeCamera.updateProjectionMatrix();
  if (composer) composer.setSize(window.innerWidth, window.innerHeight);
});

// ── Scroll-wheel zoom (desktop) ───────────────────────────────────────────────
renderer.domElement.addEventListener('wheel', (e) => {
  e.preventDefault();
  camera.adjustZoom(e.deltaY * 0.0012);
}, { passive: false });

// ── Pinch zoom (mobile) ───────────────────────────────────────────────────────
let pinchStartDist = 0;
let pinchStartZoom = 1;

renderer.domElement.addEventListener('touchstart', (e) => {
  if (e.touches.length === 2) {
    pinchStartDist = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY,
    );
    pinchStartZoom = camera.zoomFactor;
  }
}, { passive: true });

renderer.domElement.addEventListener('touchmove', (e) => {
  if (e.touches.length !== 2) return;
  e.preventDefault();
  const dist = Math.hypot(
    e.touches[0].clientX - e.touches[1].clientX,
    e.touches[0].clientY - e.touches[1].clientY,
  );
  camera.setZoom(pinchStartZoom * (pinchStartDist / dist));
}, { passive: false });

// ── Zoom buttons (+/−) ────────────────────────────────────────────────────────
document.getElementById('btn-zoom-in') ?.addEventListener('pointerdown', () => camera.adjustZoom(-0.25));
document.getElementById('btn-zoom-out')?.addEventListener('pointerdown', () => camera.adjustZoom( 0.33));

// ── World state ───────────────────────────────────────────────────────────────
let worldGroup  = null;
let waterMesh   = null; // animated water — stored separately for time updates
let elevFn      = null;
let loadedProj  = createProjection(DEFAULT_LAT, DEFAULT_LNG);
let isLoading   = false;
let totalTime   = 0;

// ── loadArea ──────────────────────────────────────────────────────────────────
async function loadArea(lat, lng, { preserveCar = false, savedCarX = 0, savedCarZ = 0 } = {}) {

  // ── 1. Fetch data — OLD world stays visible during this await ─────────────
  const [rawData, newElevFn] = await Promise.all([
    fetchOSMData(lat, lng, LOAD_RADIUS_METERS),
    fetchElevation(lat, lng, LOAD_RADIUS_METERS),
  ]);

  const newProj = createProjection(lat, lng);
  const { roads, buildings, areas, trees, treeRows, railways, barriers, fountains, coastlines, trafficSignals }
    = parseOSMData(rawData);

  // ── 2. Build geometry ─────────────────────────────────────────────────────
  const newTerrain    = createTerrain(3000, newElevFn);
  const newSea        = buildSeaMesh(coastlines, newProj, LOAD_RADIUS_METERS);
  const areaMesh      = buildAreaMesh(areas, newProj, newElevFn);
  const newWaterMesh  = buildWaterMesh(areas, newProj, newElevFn);
  const roadMesh      = buildRoadMesh(roads, newProj, newElevFn);
  const railwayMesh   = buildRailwayMesh(railways, newProj, newElevFn);
  const barrierMesh   = buildBarrierMesh(barriers, newProj, newElevFn);
  const buildingMesh  = buildBuildingMesh(buildings, newProj, newElevFn);
  const bridgeMesh    = buildBridgeMesh(roads, newProj, newElevFn);
  const treeMesh      = buildTreeMesh(trees, treeRows, newProj, newElevFn);
  const amenityMesh       = buildAmenityMesh(fountains, buildings, newProj, newElevFn);
  const trafficLightsMesh = buildTrafficLightsMesh(trafficSignals, newProj, newElevFn);

  const newWorldGroup = new THREE.Group();
  newWorldGroup.add(areaMesh, roadMesh, railwayMesh, barrierMesh,
                    buildingMesh, bridgeMesh, treeMesh, amenityMesh, trafficLightsMesh);
  if (newWaterMesh) newWorldGroup.add(newWaterMesh);

  // ── 3. Atomic swap ────────────────────────────────────────────────────────
  if (worldGroup) { scene.remove(worldGroup); disposeGroup(worldGroup); }
  scene.remove(terrainMesh);
  disposeGroup(terrainMesh);
  if (seaMesh) { scene.remove(seaMesh); disposeGroup(seaMesh); }

  terrainMesh = newTerrain;
  seaMesh     = newSea;
  worldGroup  = newWorldGroup;
  waterMesh   = newWaterMesh;
  elevFn      = newElevFn;
  loadedProj  = newProj;
  car.elevFn  = elevFn;

  scene.add(terrainMesh);
  if (seaMesh) scene.add(seaMesh);
  scene.add(worldGroup);

  // ── 4. Collision segments (buildings + barriers) ──────────────────────────
  car.collidables = buildCollidables(buildings, barriers, newProj);

  // ── 5. Traffic ────────────────────────────────────────────────────────────
  traffic.load(roads, newProj, newElevFn);

  // ── 6. Mini-map ───────────────────────────────────────────────────────────
  if (minimap) minimap.setCenter(lat, lng, LOAD_RADIUS_METERS);

  // ── 7. Car placement ──────────────────────────────────────────────────────
  if (preserveCar) {
    car.x -= savedCarX;
    car.z -= savedCarZ;
    camera.shiftOrigin(savedCarX, savedCarZ);
  } else {
    const start = findStartPosition(roads, newProj);
    car.reset(start.x, start.z, start.angle);
  }

  car.elevFn = elevFn;
  carMesh.sync(car.state, elevFn);
  camera.update(car.state, 0, elevFn);
}

// ── UI ────────────────────────────────────────────────────────────────────────
const ui = initUI(async (lat, lng) => {
  isLoading = true;
  try {
    await loadArea(lat, lng);
  } finally {
    isLoading = false;
  }
});

// ── Settings panel ────────────────────────────────────────────────────────────
{
  const settingsBtn   = document.getElementById('settings-btn');
  const settingsPanel = document.getElementById('settings-panel');
  const speedSlider   = document.getElementById('setting-max-speed');
  const speedValEl    = document.getElementById('setting-max-speed-val');
  const collisionsCheck = document.getElementById('setting-collisions');

  settingsBtn?.addEventListener('click', () => {
    const open = settingsPanel.classList.toggle('visible');
    settingsBtn.classList.toggle('active', open);
  });

  function applyMaxSpeed(kmh) {
    car.maxSpeed = kmh / 3.6;
    if (speedValEl) speedValEl.textContent = `${kmh} km/h`;
    if (speedSlider) speedSlider.value = kmh;
    const sideSlider = document.getElementById('side-max-speed');
    const sideVal    = document.getElementById('side-max-speed-val');
    if (sideSlider) sideSlider.value = kmh;
    if (sideVal)    sideVal.textContent = `${kmh} km/h`;
  }

  function applyCollisions(checked) {
    car.noClip = !checked;
    if (collisionsCheck) collisionsCheck.checked = checked;
    const sideCheck = document.getElementById('side-collisions');
    if (sideCheck) sideCheck.checked = checked;
  }

  speedSlider?.addEventListener('input', () =>
    applyMaxSpeed(parseInt(speedSlider.value, 10)));

  collisionsCheck?.addEventListener('change', () =>
    applyCollisions(collisionsCheck.checked));

  document.getElementById('side-max-speed')?.addEventListener('input', (e) =>
    applyMaxSpeed(parseInt(e.target.value, 10)));

  document.getElementById('side-collisions')?.addEventListener('change', (e) =>
    applyCollisions(e.target.checked));
}

// ── Day/Night UI ──────────────────────────────────────────────────────────────
{
  const timeSlider     = document.getElementById('setting-time');
  const timeValEl      = document.getElementById('setting-time-val');
  const timeAuto       = document.getElementById('setting-time-auto');
  const sideTimeSlider = document.getElementById('side-time');
  const sideTimeVal    = document.getElementById('side-time-val');
  const sideTimeAuto   = document.getElementById('side-time-auto');

  function fmtHour(h) {
    const hh = Math.floor(h) % 24;
    const mm = Math.floor((h % 1) * 60);
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }

  function applyTime(h) {
    daynight.setHour(h);
    const label = fmtHour(h);
    if (timeValEl)      timeValEl.textContent = label;
    if (sideTimeVal)    sideTimeVal.textContent = label;
    if (timeSlider)     timeSlider.value = h;
    if (sideTimeSlider) sideTimeSlider.value = h;
  }

  function disableAuto() {
    daynight.setRealTime(false);
    if (timeAuto)     timeAuto.checked = false;
    if (sideTimeAuto) sideTimeAuto.checked = false;
  }

  timeSlider?.addEventListener('input', () => {
    disableAuto();
    applyTime(parseFloat(timeSlider.value));
  });
  sideTimeSlider?.addEventListener('input', () => {
    disableAuto();
    applyTime(parseFloat(sideTimeSlider.value));
  });

  function setAutoMode(on) {
    daynight.setRealTime(on);
    if (timeAuto)     timeAuto.checked = on;
    if (sideTimeAuto) sideTimeAuto.checked = on;
  }

  timeAuto?.addEventListener('change',     () => setAutoMode(timeAuto.checked));
  sideTimeAuto?.addEventListener('change', () => setAutoMode(sideTimeAuto.checked));

  // Initialise display
  applyTime(12);
}

// ── Weather UI ────────────────────────────────────────────────────────────────
{
  const weatherSel     = document.getElementById('setting-weather');
  const sideWeatherSel = document.getElementById('side-weather');

  function applyWeather(type) {
    weather.setType(type);
    if (weatherSel)     weatherSel.value = type;
    if (sideWeatherSel) sideWeatherSel.value = type;
  }

  weatherSel?.addEventListener('change',     () => applyWeather(weatherSel.value));
  sideWeatherSel?.addEventListener('change', () => applyWeather(sideWeatherSel.value));
}

// ── SSAO toggle ───────────────────────────────────────────────────────────────
{
  const ssaoCheck = document.getElementById('setting-ssao');
  ssaoCheck?.addEventListener('change', () => {
    useSSAO = ssaoCheck.checked;
    if (useSSAO && !composer) _buildComposer();
  });
}

// ── Airplane mode toggle ──────────────────────────────────────────────────────
const flyBtn = document.getElementById('btn-fly');

function toggleFly() {
  car.flying = !car.flying;
  if (!car.flying) { car.y = 0; car.altSpeed = 0; }
  flyBtn?.classList.toggle('active', car.flying);
  document.getElementById('side-fly-btn')?.classList.toggle('active', car.flying);
  document.getElementById('hud-fly')?.style.setProperty('display', car.flying ? 'block' : 'none');
  document.getElementById('fly-touch-cluster')?.classList.toggle('visible', car.flying);
}

flyBtn?.addEventListener('click', toggleFly);

window.addEventListener('keydown', (e) => {
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
  if (e.code === 'KeyF') toggleFly();
});

// ── Load default area on startup ──────────────────────────────────────────────
isLoading = true;
ui.showLoading('Loading OSM data…');
loadArea(DEFAULT_LAT, DEFAULT_LNG)
  .catch((err) => {
    console.error('Failed to load default area:', err);
    ui.showError('Could not load default area. Try searching for a city.');
  })
  .finally(() => {
    isLoading = false;
    ui.hideLoading();
  });

// ── Render loop ───────────────────────────────────────────────────────────────
const clock = new THREE.Clock();

function animate() {
  requestAnimationFrame(animate);

  const rawDt = clock.getDelta();
  const dt    = Math.min(rawDt, 0.05);
  totalTime  += dt;

  controls.update();
  car.update(dt, controls.state);
  carMesh.sync(car.state, elevFn);
  camera.update(car.state, dt, elevFn);
  traffic.update(dt);

  // Day/night cycle
  daynight.update();

  // Animated water
  if (waterMesh?.material?.uniforms?.time) {
    waterMesh.material.uniforms.time.value = totalTime;
  }

  // Weather particles
  weather.update(dt, car.state.x, car.state.y ?? 0, car.state.z);

  if (minimap) minimap.draw(car.state, loadedProj);

  // Shadow frustum follows car
  sun.target.position.set(car.state.x, 0, car.state.z);
  sun.target.updateMatrixWorld();

  ui.updateHUD(car.state);
  _checkWorldBounds();

  if (useSSAO && composer) {
    composer.render();
  } else {
    renderer.render(scene, camera.threeCamera);
  }
}
animate();

// ── Auto world reload ─────────────────────────────────────────────────────────

function _checkWorldBounds() {
  if (isLoading) return;

  const { x, z } = car.state;
  const dist = Math.sqrt(x * x + z * z);
  if (dist <= WORLD_RELOAD_TRIGGER) return;

  const savedCarX = x;
  const savedCarZ = z;
  const { lat, lon } = loadedProj.unproject(savedCarX, savedCarZ);

  isLoading = true;
  ui.setStatus('⟳ Loading nearby area…');

  loadArea(lat, lon, { preserveCar: true, savedCarX, savedCarZ })
    .catch((err) => {
      console.warn('Auto-reload failed:', err);
      ui.showError('Could not load nearby area.');
    })
    .finally(() => {
      isLoading = false;
    });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function findStartPosition(roads, proj) {
  const PREFERRED = new Set(['residential', 'secondary', 'tertiary', 'primary']);

  for (const pass of [PREFERRED, null]) {
    let best     = null;
    let bestDist = Infinity;

    for (const road of roads) {
      if (pass && !pass.has(road.highway)) continue;
      if (!road.nodes || road.nodes.length < 2) continue;

      for (let i = 0; i < road.nodes.length - 1; i++) {
        const n0 = road.nodes[i];
        const n1 = road.nodes[i + 1];
        if (!n0?.lat || !n1?.lat) continue;

        const p0 = proj.project(n0.lat, n0.lon);
        const p1 = proj.project(n1.lat, n1.lon);
        if (!isFinite(p0.x) || !isFinite(p0.z)) continue;

        const mx   = (p0.x + p1.x) / 2;
        const mz   = (p0.z + p1.z) / 2;
        const dist = mx * mx + mz * mz;

        if (dist < bestDist) {
          bestDist = dist;
          const dx = p1.x - p0.x, dz = p1.z - p0.z;
          best = { x: mx, z: mz, angle: Math.atan2(dx, dz) };
        }
      }
    }

    if (best) return best;
  }

  return { x: 0, z: 0, angle: 0 };
}

function buildCollidables(buildings, barriers, proj) {
  const segs = [];

  for (const bldg of buildings) {
    if (!bldg.nodes?.length) continue;
    for (let i = 0; i < bldg.nodes.length - 1; i++) {
      const a = proj.project(bldg.nodes[i].lat, bldg.nodes[i].lon);
      const b = proj.project(bldg.nodes[i + 1].lat, bldg.nodes[i + 1].lon);
      segs.push({ ax: a.x, az: a.z, bx: b.x, bz: b.z });
    }
  }

  for (const barrier of barriers) {
    if (!barrier.nodes?.length) continue;
    for (let i = 0; i < barrier.nodes.length - 1; i++) {
      const a = proj.project(barrier.nodes[i].lat, barrier.nodes[i].lon);
      const b = proj.project(barrier.nodes[i + 1].lat, barrier.nodes[i + 1].lon);
      segs.push({ ax: a.x, az: a.z, bx: b.x, bz: b.z });
    }
  }

  return segs;
}

function disposeGroup(obj) {
  obj.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) {
      if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
      else child.material.dispose();
    }
  });
}
